import React from 'react';
import { Link } from 'react-router-dom';
import { Button, Icon, Card } from '@blueprintjs/core';
import type { BenefitCondition } from '../../types';
import './LivingBenefitsPage.scss';

const benefitConditions: BenefitCondition[] = [
  {
    id: '1',
    category: 'critical-illness',
    title: 'Critical Illness',
    description: 'When the diagnosis hits, so do the bills. Your policy steps up when you need it most.',
    examples: ['Cancer (invasive)', 'Heart Attack', 'Stroke', 'Major Organ Transplant', 'Kidney Failure', 'ALS'],
  },
  {
    id: '2',
    category: 'chronic',
    title: 'Chronic Illness',
    description: 'If daily tasks become impossible, your family shouldn\'t have to choose between your care and financial survival.',
    examples: [
      'Unable to perform 2 of 6 ADLs (bathing, dressing, toileting, transferring, continence, eating)',
      'Severe cognitive impairment requiring substantial supervision',
    ],
  },
  {
    id: '3',
    category: 'critical-injury',
    title: 'Critical Injury',
    description: 'Accidents don\'t send warning letters. Neither should your insurance leave you unprepared.',
    examples: ['Paralysis (quadriplegia, paraplegia)', 'Coma', 'Major Burns (severe burns covering significant body area)', 'Traumatic Brain Injury'],
  },
];

const LivingBenefitsPage: React.FC = () => {
  return (
    <div className="living-benefits-page">
      {/* Hero Section */}
      <section className="living-benefits-page__hero section--gradient">
        <div className="container">
          <div className="living-benefits-page__hero-content">
            <span className="living-benefits-page__eyebrow">The New Standard</span>
            <h1>Living Benefits Explained</h1>
            <p>
              Life insurance that does more than just pay when you die. Living Benefits give you
              access to your death benefit while you're still alive — when facing serious illness or injury.
            </p>
          </div>
        </div>
      </section>

      {/* What Are Living Benefits */}
      <section className="section">
        <div className="container">
          <div className="living-benefits-page__intro">
            <h2>Let's Talk About Living Benefits</h2>
            <p className="living-benefits-page__intro-lead">
              Here's something most people don't realize: <strong>life insurance can pay you while you're
              still alive</strong>. It's called Living Benefits, and it could be the difference between
              financial ruin and financial security if you ever face a serious illness.
            </p>
            <p className="living-benefits-page__intro-lead">
              Living Benefits policies include something called <strong>Accelerated Benefit Riders (ABRs)</strong>.
              In plain English? If you get diagnosed with cancer, have a heart attack, or face another
              serious health event, you can access a portion of your death benefit right then and there —
              when you actually need the money.
            </p>
            <div className="living-benefits-page__highlight">
              <Icon icon="info-sign" size={24} />
              <p>
                <strong>Here's the best part:</strong> Top Living Benefits products include these riders at
                <strong>no extra cost</strong>. Same price as traditional life insurance, but you get
                so much more protection. Why would anyone settle for less?
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="section section--light">
        <div className="container">
          <div className="text-center mb-5">
            <h2>How Does It Actually Work?</h2>
            <p className="living-benefits-page__section-subtitle">
              It's simpler than you might think. Here's what happens when you need your benefits.
            </p>
          </div>
          <div className="living-benefits-page__steps">
            <div className="living-benefits-page__step">
              <div className="living-benefits-page__step-number">1</div>
              <h3>Get Protected</h3>
              <p>
                We set you up with a life insurance policy that includes Living Benefits.
                Same price as the old-fashioned kind, but way more useful.
              </p>
            </div>
            <div className="living-benefits-page__step-arrow">
              <Icon icon="arrow-right" size={24} />
            </div>
            <div className="living-benefits-page__step">
              <div className="living-benefits-page__step-number">2</div>
              <h3>Life Happens</h3>
              <p>
                You're diagnosed with a covered illness or suffer a serious injury.
                This is when traditional insurance would leave you hanging.
              </p>
            </div>
            <div className="living-benefits-page__step-arrow">
              <Icon icon="arrow-right" size={24} />
            </div>
            <div className="living-benefits-page__step">
              <div className="living-benefits-page__step-number">3</div>
              <h3>File Your Claim</h3>
              <p>
                You submit your claim with medical documentation.
                The insurance company reviews it and determines your benefit amount.
              </p>
            </div>
            <div className="living-benefits-page__step-arrow">
              <Icon icon="arrow-right" size={24} />
            </div>
            <div className="living-benefits-page__step">
              <div className="living-benefits-page__step-number">4</div>
              <h3>Get Your Money</h3>
              <p>
                You receive a portion of your death benefit — tax-free.
                Use it for medical bills, mortgage, groceries, whatever you need.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Qualifying Conditions */}
      <section className="section">
        <div className="container">
          <div className="text-center mb-5">
            <h2>What Conditions Qualify?</h2>
            <p className="living-benefits-page__section-subtitle">
              Your Living Benefits kick in for these types of health events.
              No one plans for these, but everyone should be prepared.
            </p>
          </div>
          <div className="living-benefits-page__conditions">
            {benefitConditions.map((condition) => (
              <Card key={condition.id} className="living-benefits-page__condition-card">
                <div className={`living-benefits-page__condition-icon living-benefits-page__condition-icon--${condition.category}`}>
                  <Icon
                    icon={
                      condition.category === 'critical-illness' ? 'heart-broken' :
                      condition.category === 'chronic' ? 'time' : 'flash'
                    }
                    size={28}
                  />
                </div>
                <h3>{condition.title}</h3>
                <p>{condition.description}</p>
                <ul className="living-benefits-page__condition-examples">
                  {condition.examples.map((example, index) => (
                    <li key={index}>
                      <Icon icon="tick" size={14} />
                      {example}
                    </li>
                  ))}
                </ul>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Comparison */}
      <section className="section section--light">
        <div className="container">
          <div className="text-center mb-5">
            <h2>The Difference Is Clear</h2>
            <p className="living-benefits-page__section-subtitle">
              Side by side, it's obvious which type of insurance actually has your back.
            </p>
          </div>
          <div className="living-benefits-page__comparison-table">
            <table>
              <thead>
                <tr>
                  <th>Feature</th>
                  <th className="living-benefits-page__table-old">Traditional Life Insurance</th>
                  <th className="living-benefits-page__table-new">Living Benefits Insurance</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Death Benefit</td>
                  <td><Icon icon="tick" /> Yes</td>
                  <td><Icon icon="tick" className="text-accent" /> Yes</td>
                </tr>
                <tr>
                  <td>Access While Living</td>
                  <td><Icon icon="cross" /> No</td>
                  <td><Icon icon="tick" className="text-accent" /> Yes</td>
                </tr>
                <tr>
                  <td>Critical Illness Protection</td>
                  <td><Icon icon="cross" /> No</td>
                  <td><Icon icon="tick" className="text-accent" /> Yes</td>
                </tr>
                <tr>
                  <td>Chronic Illness Coverage</td>
                  <td><Icon icon="cross" /> No</td>
                  <td><Icon icon="tick" className="text-accent" /> Yes</td>
                </tr>
                <tr>
                  <td>Critical Injury Benefits</td>
                  <td><Icon icon="cross" /> No</td>
                  <td><Icon icon="tick" className="text-accent" /> Yes</td>
                </tr>
                <tr>
                  <td>Tax-Free Benefits</td>
                  <td><Icon icon="tick" /> Death Only</td>
                  <td><Icon icon="tick" className="text-accent" /> Living & Death</td>
                </tr>
                <tr>
                  <td>Extra Cost</td>
                  <td>N/A</td>
                  <td><Icon icon="tick" className="text-accent" /> $0 Extra</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="section">
        <div className="container">
          <div className="text-center mb-5">
            <h2>Questions You're Probably Asking</h2>
            <p className="living-benefits-page__section-subtitle">
              These are the questions I hear most often. Let me give you the straight answers.
            </p>
          </div>
          <div className="living-benefits-page__faq">
            <div className="living-benefits-page__faq-item">
              <h3>How much of my death benefit can I actually get?</h3>
              <p>
                It depends on your situation. Typically, you can access 50-90% of your death benefit
                based on the severity of your condition. Whatever you don't use stays as a death
                benefit for your family. You're not losing anything — you're just getting access
                to it when you need it.
              </p>
            </div>
            <div className="living-benefits-page__faq-item">
              <h3>Do I have to pay taxes on this money?</h3>
              <p>
                In most cases, no. Living Benefits are generally received tax-free because they're
                treated as an advance on your death benefit. But I always recommend talking to a
                tax professional about your specific situation.
              </p>
            </div>
            <div className="living-benefits-page__faq-item">
              <h3>Can I spend the money on anything I want?</h3>
              <p>
                Yes! This isn't like health insurance that only pays doctors and hospitals.
                This is your money. Pay your mortgage, cover your bills, take care of your kids,
                even take that trip you always wanted. It's entirely up to you.
              </p>
            </div>
            <div className="living-benefits-page__faq-item">
              <h3>What if I recover? Do I have to pay it back?</h3>
              <p>
                Nope. If you beat cancer, survive that heart attack, or recover from that injury —
                congratulations! The money is still yours. You keep every penny. Your policy just
                continues with a reduced death benefit.
              </p>
            </div>
            <div className="living-benefits-page__faq-item">
              <h3>Do I have to be dying to qualify?</h3>
              <p>
                Absolutely not. While terminal illness does qualify, Living Benefits also cover
                critical illnesses, chronic conditions, and serious injuries — even when doctors
                expect you to fully recover. It's about getting you money when you need it,
                not when you're out of options.
              </p>
            </div>
            <div className="living-benefits-page__faq-item">
              <h3>How fast can I get my money?</h3>
              <p>
                Once your claim is approved, most people receive their funds within 2-4 weeks.
                The insurance companies understand that when you're facing a health crisis,
                time matters.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section section--dark">
        <div className="container">
          <div className="living-benefits-page__cta">
            <h2>Ready to Learn More?</h2>
            <p>
              If you're carrying debt, have a family depending on you, or just want to make sure
              you're protected for whatever life throws at you — let's talk.
            </p>
            <p className="living-benefits-page__cta-personal">
              I'll help you understand exactly what you need and find the right coverage for your situation.
              No pressure, no jargon, just honest answers.
            </p>
            <Link to="/contact">
              <Button
                intent="success"
                large
                rightIcon={<Icon icon="arrow-right" />}
              >
                Let's Talk About Your Coverage
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LivingBenefitsPage;
